// Search snippets: plain text plus match ranges, split into parts here and
// rendered as React text nodes. The only markup is the `<mark>` the UI adds.
//
// The text is raw user input — a candidate's message, a resume. It used to
// arrive as an HTML fragment and go through innerHTML, so an `<img onerror=…>`
// in a message ran in the recruiter's session as soon as they searched for a
// word from it.

export type HighlightPart = { text: string; hit: boolean };

/**
 * Split `text` by half-open `[start, end)` ranges in UTF-16 code units.
 *
 * Ranges are not trusted: out-of-bounds ones are clamped, empty or reversed
 * ones dropped, overlapping or touching ones merged. Joining the parts always
 * gives back `text` exactly.
 */
export function splitHighlight(
  text: string,
  ranges: readonly (readonly [number, number])[] = [],
): HighlightPart[] {
  const clean = ranges
    .map(([s, e]) => [clamp(s, text.length), clamp(e, text.length)] as const)
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);

  const parts: HighlightPart[] = [];
  let pos = 0;
  for (const [s, e] of clean) {
    if (e <= pos) continue; // entirely inside what is already marked
    const start = Math.max(s, pos);
    if (start > pos) parts.push({ text: text.slice(pos, start), hit: false });
    const last = parts[parts.length - 1];
    if (last?.hit && start === pos) last.text += text.slice(start, e);
    else parts.push({ text: text.slice(start, e), hit: true });
    pos = e;
  }
  if (pos < text.length) parts.push({ text: text.slice(pos), hit: false });
  return parts;
}

function clamp(n: number, max: number): number {
  return Number.isFinite(n) ? Math.min(Math.max(Math.trunc(n), 0), max) : 0;
}
